import { WorkTypesApiService } from '@adminServices';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NotificationService } from '@shared';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import * as WorkTypesActions from './work-types.actions';

@Injectable()
export class WorkTypesEffects {
  private actions$ = inject(Actions);
  private workTypesApi = inject(WorkTypesApiService);
  private notification = inject(NotificationService);

  loadWorkTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkTypesActions.loadWorkTypes),
      switchMap(({ params }) =>
        this.workTypesApi.findAll(params).pipe(
          map((response) => WorkTypesActions.loadWorkTypesSuccess({
            workTypes: response.items,
            pagination: {
              total: response.total,
              page: response.page,
              limit: response.limit,
              totalPages: response.totalPages,
            },
          })),
          catchError((error: any) => {
            this.notification.error('admin.notifications.load_work_types_error');
            return of(WorkTypesActions.loadWorkTypesFailure({ error: error.message || 'Failed to load work types' }));
          }),
        ),
      ),
    ),
  );

  createWorkType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkTypesActions.createWorkType),
      switchMap(({ workTypeData }) =>
        this.workTypesApi.create(workTypeData).pipe(
          map((workType) => {
            this.notification.success('admin.notifications.work_type_created');
            return WorkTypesActions.createWorkTypeSuccess({ workType });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.create_work_type_error');
            return of(WorkTypesActions.createWorkTypeFailure({ error: error.message || 'Failed to create work type' }));
          }),
        ),
      ),
    ),
  );

  updateWorkType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkTypesActions.updateWorkType),
      switchMap(({ id, workTypeData }) =>
        this.workTypesApi.update(id, workTypeData).pipe(
          map((workType) => {
            this.notification.success('admin.notifications.work_type_updated');
            return WorkTypesActions.updateWorkTypeSuccess({ workType });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.update_work_type_error');
            return of(WorkTypesActions.updateWorkTypeFailure({ error: error.message || 'Failed to update work type' }));
          }),
        ),
      ),
    ),
  );

  deleteWorkType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkTypesActions.deleteWorkType),
      switchMap(({ id }) =>
        this.workTypesApi.delete(id).pipe(
          map(() => {
            this.notification.success('admin.notifications.work_type_deleted');
            return WorkTypesActions.deleteWorkTypeSuccess({ id });
          }),
          catchError((error: any) => {
            this.notification.error('admin.notifications.delete_work_type_error');
            return of(WorkTypesActions.deleteWorkTypeFailure({ error: error.message || 'Failed to delete work type' }));
          }),
        ),
      ),
    ),
  );
}
